/**
 * /dashboard — DSH-059 client-facing home (R1.8 sub-step 3).
 *
 * Replaces the R1.1 placeholder greeting with a real dashboard. Sections:
 *   1. Greeting + today's date.
 *   2. Today's checklist — 7 ritual flags (read-only here; client logs them
 *      via /check-in or per-tool surfaces).
 *   3. Fulfillment Score — today's score + 7-day sparkline.
 *   4. Streak — current / longest / total days tracked.
 *   5. Weekly #1 Focus — pulled from latest weekly_review.nextWeekFocus.
 *   6. Next session + upcoming milestones (sealed letter unseal, 90-day).
 *   7. KPI trend (Energy / Work / Love over last 30 days).
 *
 * Read endpoint: `GET /api/v1/clients/me/dashboard` (resolves to the
 * authenticated client's row server-side).
 *
 * @see DSH-059
 */

import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '@/app/auth-context';
import { ProgramTimeline } from '@/components/program-timeline';
import { CelebrationsCard } from '@/components/celebrations-card';
import { SmartInsightsCard } from '@/components/smart-insights-card';
import { MonthlyTrackingGrid } from '@/components/monthly-tracking-grid';
import { DailyQuoteCard } from '@/components/daily-quote-card';
import { ConfidenceLogDashboardCard } from '@/components/confidence-log-dashboard-card';
import { WeeklyFocusPicker } from '@/components/weekly-focus-picker';
import { WinCelebration } from '@/components/win-celebration';
import { PostGradBanner } from '@/components/post-grad-banner';
import * as api from '@/lib/api-client';
import { useClientStep } from '@/lib/use-client-step';

function pickGreeting(now: Date): 'morning' | 'afternoon' | 'evening' {
  const hour = now.getHours();
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

export function DashboardPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { user, accessToken } = useAuth();
  const greetingKey = pickGreeting(new Date());
  const firstName = user?.firstName ?? user?.email.split('@')[0] ?? '';
  // CodeFix-V1 #46 — derive the date-format locale from i18n.language
  // so the dashboard header date matches the chosen UI language
  // instead of falling back to the browser default.
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';

  const [data, setData] = useState<api.ClientDashboard | null>(null);
  const [timeline, setTimeline] = useState<api.ProgramTimeline | null>(null);
  // F-016 (2026-05-22): tracking items + values for the monthly grid.
  const [trackingItems, setTrackingItems] = useState<api.TrackingItem[]>([]);
  const [trackingValues, setTrackingValues] = useState<api.TrackingValue[]>([]);
  // NL-034 — today's WIN, read from the daily_tracking row so the
  // celebration banner can flash on the FIRST dashboard view of a
  // day that has the WIN already saved (typical flow: client filled
  // /check-in WIN earlier, now lands on dashboard).
  const [todayWin, setTodayWin] = useState<string>('');
  const todayIso = new Date().toISOString().slice(0, 10);
  /**
   * NL-047 — coach-set graduation date. Drives the post-grad banner's
   * 7-day visibility window. Loaded from getClientSelf alongside the
   * existing dashboard data.
   */
  const [graduationDate, setGraduationDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The dashboard is "my own" for a signed-in client — resolve the
  // clientId from the authenticated user's profile instead of sending
  // the literal `"me"` placeholder to the API. The prod API validates
  // `:clientId` against `/^\d+$/` and rejects `"me"` with 400, which
  // surfaced as "no tools showing" for real clients (the demo server
  // accepts `me` so the dev path masked the bug).
  //
  // Note: we do NOT gate on `accessToken` — after CodeFix V1 #33 the
  // web auth runs in cookie-only mode, so `accessToken` is the empty
  // string on a cold page load. The HttpOnly cookie carries the auth;
  // `apiRequest` only adds an Authorization header when accessToken
  // is non-empty.
  const clientIdForApi = user?.clientId ?? null;

  useEffect(() => {
    if (!clientIdForApi) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        // F-016: month range for the new monthly grid.
        const now = new Date();
        const monthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
          .toISOString()
          .slice(0, 10);
        const monthEnd = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0))
          .toISOString()
          .slice(0, 10);
        const [dash, tl, items, values, todayDaily, self] = await Promise.all([
          api.getClientDashboard(accessToken ?? '', clientIdForApi),
          api.getProgramTimeline(accessToken ?? '', clientIdForApi),
          api.listTrackingItems(accessToken ?? '', clientIdForApi),
          api.getTrackingValues(accessToken ?? '', clientIdForApi, {
            from: monthStart,
            to: monthEnd,
          }),
          // NL-034 — today's daily_tracking row, for the WIN celebration.
          // Best-effort: if it 404s or fails we just don't show the banner.
          api
            .listDailyTracking(accessToken ?? '', clientIdForApi, { date: todayIso })
            .catch(() => ({ data: [] })),
          // NL-047 — getClientSelf carries the graduation_date that
          // drives the post-grad banner. Best-effort: failure = no
          // banner.
          api.getClientSelf(accessToken ?? '', clientIdForApi).catch(() => null),
        ]);
        if (active) {
          setData(dash);
          setTimeline(tl);
          setTrackingItems(items);
          setTrackingValues(values);
          // NL-034 — pre-fill the WIN. Empty string = no celebration banner.
          setTodayWin(todayDaily.data[0]?.daily_win_text ?? '');
          // NL-047 — pre-fill the graduation date (null if not graduated).
          setGraduationDate(self?.actual_graduation ?? null);
        }
      } catch {
        if (active) setError(t('dashboard.errors.loadFailed'));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientIdForApi, t, todayIso]);

  return (
    <section className="max-w-5xl mx-auto px-s5 py-s7" data-testid="dashboard">
      {/* NL-034 — WIN celebration banner. Default once-per-day guard: if
          this client already saw it today (e.g. they saved on /check-in
          and the banner flashed there), this read is a no-op. */}
      {clientIdForApi ? (
        <WinCelebration clientId={clientIdForApi} date={todayIso} winText={todayWin} />
      ) : null}
      {/* NL-047 — post-graduation banner. Self-gates on the 7-day
          window after `actual_graduation`. */}
      <div className="mb-s5">
        <PostGradBanner graduationDate={graduationDate} />
      </div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {new Date().toLocaleDateString(locale, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t(`dashboard.greetingTimes.${greetingKey}`)}
          {firstName ? `, ${firstName}.` : '.'}
        </h1>
        <p className="mt-s3 text-lg text-gray-2 max-w-prose">{t('dashboard.subtitle')}</p>
      </header>

      {loading && (
        <p className="text-gray-2" role="status">
          {t('common.loading')}
        </p>
      )}
      {error && (
        <p className="text-flame-deep" role="alert">
          {error}
        </p>
      )}

      {/* G (2026-05-22): "From your coach" lives inside the same
          `space-y-s5` wrapper as every other card so the gap below it
          matches the gap between the other cards (was visually pinched
          previously because it sat outside the spacing scope). */}
      {(data ?? clientIdForApi) && (
        <div className="space-y-s5">
          {clientIdForApi && (
            <FromCoachCard accessToken={accessToken ?? ''} clientId={clientIdForApi} />
          )}
          {data && clientIdForApi && (
            <>
              {data.unreadMessageCount > 0 && (
                <NewMessagesCard unreadCount={data.unreadMessageCount} />
              )}
              <CelebrationsCard
                accessToken={accessToken ?? ''}
                clientId={clientIdForApi}
                celebrations={data.pendingCelebrations}
              />
              <SmartInsightsCard insights={data.smartInsights} />
              {/* F-016 follow-up (2026-05-22): DailyQuoteCard moved INTO
                  the 2-col grid to fill the empty slot next to NextUp,
                  so the full-width quote slab is gone and the candy
                  monthly grid sits cleaner under the cards. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-s5">
                <FulfillmentCard data={data} />
                <StreakCard data={data} />
                <WeeklyFocusCard data={data} clientId={clientIdForApi ?? ''} />
                {/* The KPI-trend card was retired here — its per-day data
                    already lives in the monthly tracker below. The slot now
                    holds the Confidence Log, which moves off the Check-In once
                    the client has written today's line. */}
                <ConfidenceLogDashboardCard clientId={clientIdForApi ?? ''} />
                <NextUpCard data={data} />
                <DailyQuoteCard />
              </div>
              <MonthlyTrackingGrid
                items={trackingItems}
                values={trackingValues}
                kpiTrend={data.kpiTrend30d}
                todayIso={new Date().toISOString().slice(0, 10)}
                targetKpis={data.targetKpis ?? null}
              />
              {timeline && <ProgramTimeline data={timeline} />}
            </>
          )}
        </div>
      )}
    </section>
  );
}

/* ─────────────────────────────  Cards  ───────────────────────────── */

function NewMessagesCard({ unreadCount }: { unreadCount: number }): JSX.Element {
  const { t } = useTranslation();
  const label =
    unreadCount === 1
      ? t('dashboard.newMessages.one')
      : t('dashboard.newMessages.many', { count: unreadCount });
  return (
    <article
      className="bg-flame-glow rounded-lg p-s5 border border-flame/40 flex items-center justify-between gap-s4"
      data-testid="card-new-messages"
    >
      <div className="flex items-start gap-s3">
        <span
          aria-hidden="true"
          className="flex items-center justify-center w-10 h-10 rounded-full bg-flame text-bone text-lg font-semibold"
        >
          {unreadCount}
        </span>
        <div>
          <h2 className="font-display text-display-sm text-ink">
            {t('dashboard.newMessages.title')}
          </h2>
          <p className="text-sm text-gray-2">{label}</p>
        </div>
      </div>
      <Link
        to="/messages"
        className="btn-primary text-sm"
        data-testid="new-messages-cta"
      >
        {t('dashboard.newMessages.cta')}
      </Link>
    </article>
  );
}

function Card(props: {
  titleKey: string;
  children: React.ReactNode;
  testId?: string;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <article
      className="bg-bone-2 rounded-lg p-s5 border border-bone-3 shadow-sm"
      data-testid={props.testId}
    >
      <h2 className="font-display text-display-sm text-ink mb-s3">{t(props.titleKey)}</h2>
      {props.children}
    </article>
  );
}

// F-016 (2026-05-22): ChecklistCard retired. The monthly tracking grid
// shows the same items + every prior day's state in one view. The empty-
// state hint also goes there. Function kept commented for git-archaeology
// in case we ever want to bring back a "today only" glance card.

function FulfillmentCard({ data }: { data: api.ClientDashboard }): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // A (2026-05-22): the whole card is a Link into /check-in — clicking
  // anywhere opens today's ritual + KPIs.
  // #12 (2026-06-15): the per-day bars are now individual jump targets —
  // hovering shows that day's %, clicking opens the check-in for THAT day.
  // The surrounding card still opens today's check-in.
  const onPickDay = useCallback(
    (isoDate: string) => navigate(`/check-in?date=${isoDate}`),
    [navigate],
  );
  return (
    <Link
      to="/check-in"
      className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flame"
      data-testid="card-fulfillment-link"
      aria-label={t('dashboard.cards.fulfillment.openCheckInAria')}
    >
      <Card titleKey="dashboard.cards.fulfillment.title" testId="card-fulfillment">
        {/* The headline is the rolling 7-day average (a single day is noisy);
            the sparkline still shows the per-day trend underneath. Falls back
            to today's value if an older API doesn't supply the average. */}
        <p className="text-display-lg font-display text-ink">
          {(() => {
            const headline = data.fulfillment7dAvg ?? data.fulfillmentToday;
            return headline !== null && headline !== undefined
              ? `${headline}%`
              : t('dashboard.cards.fulfillment.empty');
          })()}
        </p>
        <p className="text-sm text-gray-2 mt-s2">{t('dashboard.cards.fulfillment.subtitle')}</p>
        <Sparkline points={data.fulfillmentTrend7d} onPickDate={onPickDay} />
      </Card>
    </Link>
  );
}

function StreakCard({ data }: { data: api.ClientDashboard }): JSX.Element {
  const { t } = useTranslation();
  return (
    <Card titleKey="dashboard.cards.streak.title" testId="card-streak">
      <p className="text-display-lg font-display text-ink">
        {data.streak.currentStreak}
        <span className="text-base text-gray-2 ml-s2">
          {t('dashboard.cards.streak.unit', { count: data.streak.currentStreak })}
        </span>
      </p>
      <p className="text-sm text-gray-2 mt-s2">
        {t('dashboard.cards.streak.longest', { longest: data.streak.longestStreak })}
        {' · '}
        {t('dashboard.cards.streak.total', { total: data.streak.totalDays })}
      </p>
    </Card>
  );
}

/** Monday of the ISO week containing `now` — YYYY-MM-DD in UTC. */
function isoMondayUtc(now: Date = new Date()): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const day = d.getUTCDay() === 0 ? 7 : d.getUTCDay();
  d.setUTCDate(d.getUTCDate() - (day - 1));
  return d.toISOString().slice(0, 10);
}

function WeeklyFocusCard({
  data,
  clientId,
}: {
  data: api.ClientDashboard;
  clientId: string;
}): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  // NL-030 — render the locked-state body instead of the live focus
  // UI for clients still on Steps 0-4. Coaches (isClient=false) always
  // see the live card.
  const { step: clientStep, isClient } = useClientStep();
  const isLocked = isClient && (clientStep ?? 0) < 5;

  // The Weekly Focus now lives HERE (it left the Check-in once set) and the
  // client can change it inline. We fetch the current focus + the Carpe Diem
  // virtues so the inline picker can open without another round trip.
  const [focus, setFocus] = useState<api.WeeklyFocusEntry | null>(null);
  const [virtues, setVirtues] = useState<api.AffirmationGroupEntry[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [editing, setEditing] = useState(false);
  const weekStart = useMemo(() => isoMondayUtc(), []);

  useEffect(() => {
    if (!clientId || isLocked) {
      setHydrated(true);
      return;
    }
    let active = true;
    void (async () => {
      try {
        const [f, aff] = await Promise.all([
          api.getWeeklyFocus(accessToken, clientId),
          api.getAffirmationsByGroup(accessToken, clientId),
        ]);
        if (!active) return;
        setFocus(f.current);
        setVirtues(aff.inMatrix);
      } catch {
        if (active) setFocus(null);
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId, isLocked]);

  const hasVirtue = focus?.virtueId != null && focus.virtueName !== null;

  if (isLocked) {
    return (
      <Card titleKey="dashboard.cards.weeklyFocus.title" testId="card-weekly-focus">
        <div className="flex items-start gap-s3" data-testid="weekly-focus-dashboard-locked">
          <span aria-hidden="true" className="text-flame-deep text-xl mt-s1">🔒</span>
          <p className="text-sm text-gray-2">
            {t('client.locked.section.weeklyFocus.body', { step: 5 })}
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card titleKey="dashboard.cards.weeklyFocus.title" testId="card-weekly-focus">
      {editing && virtues.length > 0 ? (
        <WeeklyFocusPicker
          clientId={clientId}
          weekStart={weekStart}
          virtues={virtues}
          current={focus}
          onSaved={(saved) => {
            setFocus(saved);
            setEditing(false);
          }}
        />
      ) : hasVirtue && focus ? (
        <div className="space-y-s2" data-testid="weekly-focus-dashboard-virtue">
          <div className="flex items-baseline justify-between">
            <p className="font-display text-display-sm text-ink border-l-4 border-flame pl-s3">
              {focus.virtueName}
            </p>
            {virtues.length > 0 && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-xs text-flame-deep hover:text-flame shrink-0"
                data-testid="weekly-focus-dashboard-change"
              >
                {t('weeklyFocus.change')}
              </button>
            )}
          </div>
          {focus.affirmationText && (
            <p className="text-sm text-ink italic pl-s3">
              “{focus.affirmationText}”
            </p>
          )}
          {focus.reason && (
            <p
              className="text-sm text-gray-2 pl-s3"
              data-testid="weekly-focus-dashboard-reason"
            >
              {focus.reason}
            </p>
          )}
        </div>
      ) : data.weeklyFocus ? (
        <p
          className="text-base text-ink leading-relaxed border-l-4 border-flame pl-s3"
          data-testid="weekly-focus-dashboard-legacy"
        >
          {data.weeklyFocus}
        </p>
      ) : hydrated ? (
        <div data-testid="weekly-focus-dashboard-empty">
          <p className="text-sm text-gray-2">
            {virtues.length > 0
              ? t('dashboard.cards.weeklyFocus.empty')
              : t('weeklyFocus.needsVirtues')}
          </p>
          {virtues.length > 0 && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="btn-primary inline-flex mt-s3 text-sm"
              data-testid="weekly-focus-dashboard-pick-cta"
            >
              {t('dashboard.cards.weeklyFocus.pickCta')}
            </button>
          )}
        </div>
      ) : (
        <p className="text-sm text-gray-2">{t('common.loading')}</p>
      )}
    </Card>
  );
}

function NextUpCard({ data }: { data: api.ClientDashboard }): JSX.Element {
  const { t, i18n } = useTranslation();
  // C (2026-05-22): mirror the coach app's session card — show the
  // next session's date + time + planned step + a prominent "Join
  // Zoom" button when a video link is on file. Same spirit as the
  // macOS coach app's CompactSessionCard.
  const dateLocale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  return (
    <Card titleKey="dashboard.cards.nextUp.title" testId="card-next-up">
      <ul className="space-y-s3">
        {data.nextSessionAt && (
          <li className="space-y-s2">
            <p className="text-sm text-gray-2">
              {t('dashboard.cards.nextUp.session')}
              {typeof data.nextSessionStep === 'number' && (
                <span className="ml-s2 font-mono text-xs uppercase tracking-wide text-flame-ember">
                  {t('dashboard.cards.nextUp.step', { step: data.nextSessionStep })}
                </span>
              )}
            </p>
            <p className="text-base text-ink">
              {new Date(data.nextSessionAt).toLocaleString(dateLocale, {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </p>
            {data.nextSessionVideoLink && (
              <a
                href={data.nextSessionVideoLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-s3 py-s2 rounded-md bg-flame text-bone text-sm font-medium hover:bg-flame-deep"
                data-testid="card-next-up-join-zoom"
              >
                {t('dashboard.cards.nextUp.joinZoom')}
              </a>
            )}
          </li>
        )}
        {data.upcomingMilestones.map((m) => (
          <li key={`${m.kind}-${m.date}`}>
            <p className="text-sm text-gray-2">{t(`dashboard.milestones.${m.kind}`)}</p>
            <p className="text-base text-ink">
              {new Date(m.date).toLocaleDateString(dateLocale, { dateStyle: 'medium' })}
              {' · '}
              <span className="text-gray-1">
                {m.daysUntil >= 0
                  ? t('dashboard.milestones.daysAway', { days: m.daysUntil })
                  : t('dashboard.milestones.daysAgo', { days: Math.abs(m.daysUntil) })}
              </span>
            </p>
          </li>
        ))}
        {!data.nextSessionAt && data.upcomingMilestones.length === 0 && (
          <li className="text-sm text-gray-2">{t('dashboard.cards.nextUp.empty')}</li>
        )}
      </ul>
    </Card>
  );
}

/* ─────────────────────────────  Tiny visualisations  ───────────────────────────── */

const Sparkline = memo(function Sparkline({
  points,
  onPickDate,
}: {
  points: { date: string; score: number | null }[];
  onPickDate: (isoDate: string) => void;
}): JSX.Element {
  // Inline SVG bar chart for the 7-day fulfillment trend. Values 0-100;
  // null days render as empty placeholder bars. memo()-wrapped so the
  // SVG isn't re-built on every parent render — only when `points`
  // identity changes. #12 — each column is a hover-tooltip (that day's %)
  // + a click target into that day's check-in. Brand tokens via fill-*.
  const { t, i18n } = useTranslation();
  const max = 100;
  const width = points.length * 24;
  const height = 60;
  const fmtDay = (iso: string): string => {
    const [y, m, d] = iso.split('-');
    if (!y || !m || !d) return iso;
    return i18n.language?.startsWith('de') ? `${d}.${m}.` : `${m}/${d}`;
  };
  return (
    <svg
      role="img"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="w-full h-[60px] mt-s3"
      aria-label={t('dashboard.cards.fulfillment.trendAria')}
    >
      {points.map((p, i) => {
        const x = i * 24;
        const value = p.score ?? 0;
        const barH = (value / max) * (height - 4);
        const isNull = p.score === null;
        const tip = `${fmtDay(p.date)} · ${
          isNull ? t('dashboard.cards.fulfillment.barNoData') : `${p.score}%`
        }`;
        return (
          // #12 — preventDefault + stopPropagation keep the bar click off the
          // surrounding /check-in Link (which still opens today's check-in).
          <g
            key={p.date}
            className="cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onPickDate(p.date);
            }}
          >
            <title>{tip}</title>
            <rect
              x={x + 4}
              y={height - barH - 2}
              width={16}
              height={Math.max(barH, 2)}
              className={isNull ? 'fill-bone-3' : 'fill-flame-deep'}
              rx={2}
              pointerEvents="none"
            />
            {/* Full-height transparent hit + hover target for the column. */}
            <rect x={x} y={0} width={24} height={height} fill="transparent" />
          </g>
        );
      })}
    </svg>
  );
});

/* ─────────────────────────────  "From your coach" card  ───────────────────────────── */

/**
 * R3.6.x — surfaces tools the coach has explicitly assigned to this
 * client (status === 'not_started' rows from `GET /clients/me/tools`).
 * Clicking a row opens the same tool route the client already had —
 * we just give them an opinionated entry point.
 */
const TOOL_ROUTE: Record<string, string> = {
  // F-022 (2026-05-23) — Step 0 collapsed to two tools.
  'discovery-call': '/tools/discovery-call',
  'client-commitment': '/tools/client-commitment',
  // F-024 (2026-05-23) — Compass (Step 1 entry).
  compass: '/tools/compass',
  eulogy: '/tools/eulogy',
  superhero: '/tools/superhero',
  'values-card-sort': '/tools/values-card-sort',
  'scrambled-virtues': '/tools/scrambled-virtues',
  // #2/#10 (2026-06-13): carpe-diem (3×3 text tool) + virtue-scoring removed.
  'identity-snapshot': '/tools/identity-snapshot',
  'virtue-shortlist': '/tools/virtue-shortlist',
  'virtue-affirmations': '/tools/virtue-affirmations',
  'wheel-of-life': '/tools/wheel-of-life',
  'hero-vision': '/tools/hero-vision',
  woop: '/tools/woop',
  'energy-audit': '/tools/energy-audit',
  'masterpiece-day': '/tools/masterpiece-day',
  // batch #4 #6 — combined two-part tool; the dashboard collapses the
  // energy-audit + masterpiece-day pair into this single entry.
  'masterpiece-blueprint': '/tools/masterpiece-blueprint',
  'fixed-bookend': '/tools/fixed-bookend',
  'six-step-reframing': '/tools/six-step-reframing',
  'virtue-reassessment': '/tools/virtue-reassessment',
  'bid-for-connection': '/tools/bid-for-connection',
  'heroic-work-audit': '/tools/heroic-work-audit',
  'dragon-trigger-log': '/tools/dragon-trigger-log',
  'good-day-fallback-day': '/tools/good-day-fallback-day',
  'chaos-scenario-cards': '/tools/chaos-scenario-cards',
  'weekly-review': '/tools/weekly-review',
  'celebration-system': '/tools/celebration-system',
  'heros-journey': '/tools/heros-journey',
  'identity-gap-reassessment': '/tools/identity-gap-reassessment',
  'legacy-letter': '/tools/legacy-letter',
};

function toolDisplayName(t: (k: string) => string, toolKey: string): string {
  // Prefer the canonical tool name from the dashboard.cards.fromCoach
  // dictionary; fall back to humanized key if a new tool isn't yet listed.
  const localized = t(`dashboard.cards.fromCoach.toolName.${toolKey}`);
  if (localized && !localized.startsWith('dashboard.cards.fromCoach.toolName.')) {
    return localized;
  }
  return toolKey
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function FromCoachCard({
  accessToken,
  clientId,
}: {
  accessToken: string;
  clientId: string;
}): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const [assigned, setAssigned] = useState<api.ToolCompletion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const list = await api.listToolCompletions(accessToken, clientId);
        if (!active) return;
        // B (2026-05-22): show anything the coach has assigned that the
        // client hasn't FINISHED yet — both `not_started` AND
        // `in_progress`. Earlier the card only surfaced `not_started`,
        // so a tool that the client had partially filled in (saved a
        // draft, switched to something else) silently disappeared from
        // the "from your coach" list. Completed + skipped stay hidden.
        setAssigned(
          list.data.filter(
            (row) => row.status === 'not_started' || row.status === 'in_progress',
          ),
        );
        setError(null);
      } catch (cause) {
        if (!active) return;
        // Surface an error UI with retry instead of silently hiding
        // the card. Closes CodeFix-V1 #40.
        const message = cause instanceof Error ? cause.message : 'request failed';
        setError(message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId, retryNonce]);

  if (loading) return null;
  if (error) {
    return (
      <Card titleKey="dashboard.cards.fromCoach.title" testId="card-from-coach-error">
        <div className="flex items-center justify-between gap-s3">
          <p className="text-gray-2 text-sm">{t('dashboard.cards.fromCoach.error')}</p>
          <button
            type="button"
            onClick={() => {
              setLoading(true);
              setError(null);
              setRetryNonce((n) => n + 1);
            }}
            className="text-sm font-semibold text-flame-ember underline"
          >
            {t('common.retry')}
          </button>
        </div>
      </Card>
    );
  }
  if (assigned.length === 0) return null;

  const formatter = new Intl.RelativeTimeFormat(i18n.language === 'de' ? 'de' : 'en', {
    numeric: 'auto',
  });
  function relative(iso: string | null | undefined): string {
    if (!iso) return '';
    const date = new Date(iso);
    const diffMs = date.getTime() - Date.now();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    if (Math.abs(diffDays) >= 1) return formatter.format(diffDays, 'day');
    const diffHours = Math.round(diffMs / (1000 * 60 * 60));
    return formatter.format(diffHours, 'hour');
  }

  // batch #4 #6 — collapse the energy-audit + masterpiece-day pair into one
  // "Masterpiece Day & Energy Audit" entry pointing at the combined two-part
  // tool. Whichever (or both) the coach assigned, the client sees one entry.
  const PAIR_KEYS = new Set(['energy-audit', 'masterpiece-day']);
  const pairRows = assigned.filter((r) => PAIR_KEYS.has(r.tool_type));
  const displayRows: api.ToolCompletion[] =
    pairRows.length > 0
      ? [
          { ...pairRows[0]!, id: 'masterpiece-blueprint', tool_type: 'masterpiece-blueprint' },
          ...assigned.filter((r) => !PAIR_KEYS.has(r.tool_type)),
        ]
      : assigned;

  return (
    <article
      className="bg-bone-2 rounded-lg p-s5 border border-flame/30 shadow-sm"
      data-testid="card-from-coach"
    >
      <header className="flex items-baseline justify-between mb-s3">
        <h2 className="font-display text-display-sm text-ink">{t('dashboard.cards.fromCoach.title')}</h2>
        <span className="font-mono text-xs text-flame-ember uppercase tracking-wide">
          {displayRows.length}
        </span>
      </header>
      <p className="text-sm text-gray-2 mb-s4">{t('dashboard.cards.fromCoach.subtitle')}</p>
      <ul className="space-y-s2">
        {displayRows.map((row) => {
          const route = TOOL_ROUTE[row.tool_type];
          const name = toolDisplayName(t, row.tool_type);
          return (
            <li
              key={row.id}
              className="flex items-center justify-between p-s3 rounded-md bg-bone border border-bone-3 hover:border-flame transition-colors"
            >
              <div className="flex flex-col">
                <span className="text-ink font-medium">{name}</span>
                {row.assigned_at && (
                  <span className="text-xs text-gray-2">
                    {t('dashboard.cards.fromCoach.assignedRelative', {
                      relative: relative(row.assigned_at),
                    })}
                  </span>
                )}
              </div>
              {route ? (
                <a
                  href={route}
                  className="inline-flex items-center px-s3 py-s2 rounded-md bg-flame text-bone text-sm font-medium hover:bg-flame-deep"
                  data-testid={`from-coach-open-${row.tool_type}`}
                >
                  {t('dashboard.cards.fromCoach.openCta')}
                </a>
              ) : null}
            </li>
          );
        })}
      </ul>
    </article>
  );
}

